import React, { useState, useMemo } from 'react';

export type Lifecycle5State = 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';

export interface TableColumn<T> {
  key: string;
  title: string;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  render?: (row: T, index: number) => React.ReactNode;
  sortValue?: (row: T) => number | string;
  sortComparator?: (a: T, b: T) => number;
}

export interface QuantitativeDataTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  state: Lifecycle5State;
  errorMessage?: string | null;
  onRetry?: () => void;
  pageSize?: number;
  rowKey: (row: T, index: number) => string;
  emptyMessage?: string;
  searchFilter?: (row: T, query: string) => boolean;
  searchPlaceholder?: string;
  headerAction?: React.ReactNode;
  title?: string;
  initialSortKey?: string | null;
  initialSortDirection?: 'asc' | 'desc';
  pageSizeOptions?: number[];
}

export function QuantitativeDataTable<T>({
  data,
  columns,
  state,
  errorMessage,
  onRetry,
  pageSize = 30,
  rowKey,
  emptyMessage = '当前无可用数据',
  searchFilter,
  searchPlaceholder = '按代码或名称筛选...',
  headerAction,
  title,
  initialSortKey = null,
  initialSortDirection = 'desc',
  pageSizeOptions = [30, 50, 100, 200],
}: QuantitativeDataTableProps<T>) {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = useState<number>(pageSize);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortKey, setSortKey] = useState<string | null>(initialSortKey ?? null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialSortDirection);

  React.useEffect(() => {
    setCurrentPageSize(pageSize);
  }, [pageSize]);

  // Filter and Sort data
  const processedData = useMemo(() => {
    let result = [...data];

    // Search filter
    if (searchQuery.trim() && searchFilter) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((item) => searchFilter(item, q));
    }

    // Sort
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      if (col && (col.sortComparator || col.sortValue)) {
        result.sort((a, b) => {
          let cmp = 0;
          if (col.sortComparator) {
            cmp = col.sortComparator(a, b);
          } else if (col.sortValue) {
            const valA = col.sortValue(a);
            const valB = col.sortValue(b);
            if (valA === valB) cmp = 0;
            else if (valA === null || valA === undefined) cmp = 1;
            else if (valB === null || valB === undefined) cmp = -1;
            else cmp = valA > valB ? 1 : -1;
          }
          return sortDirection === 'asc' ? cmp : -cmp;
        });
      }
    }

    return result;
  }, [data, searchQuery, searchFilter, sortKey, sortDirection, columns]);

  const totalPages = Math.max(1, Math.ceil(processedData.length / currentPageSize));
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * currentPageSize;
    return processedData.slice(start, start + currentPageSize);
  }, [processedData, currentPage, currentPageSize]);

  const handleSort = (colKey: string) => {
    if (sortKey === colKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(colKey);
      setSortDirection('desc');
    }
  };

  // Render 5-State Machine Content
  if (state === 'loading') {
    return (
      <div className="table-state-container loading-state" style={{ padding: '32px', textAlign: 'center' }}>
        <div style={{ fontSize: '24px', marginBottom: '12px' }}>⚡</div>
        <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>正在从 MultiAsset 节点检索时序数据，请稍候...</div>
      </div>
    );
  }

  if (state === 'disconnected') {
    return (
      <div
        className="table-state-container disconnected-state"
        style={{
          padding: '36px',
          textAlign: 'center',
          background: 'var(--alert-bearish-bg)',
          borderRadius: '8px',
          border: '1px solid var(--alert-bearish-border)',
        }}
      >
        <div style={{ fontSize: '28px', marginBottom: '8px' }}>📡</div>
        <div style={{ color: 'var(--color-bearish)', fontWeight: 600, fontSize: '14px' }}>
          数据源连接已断开 (DISCONNECTED)
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '6px', marginBottom: '16px' }}>
          {errorMessage || '后端 MultiAsset 存储节点响应超时或未就绪'}
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            style={{
              padding: '6px 16px',
              borderRadius: '6px',
              background: 'var(--btn-primary-bg)',
              color: 'var(--btn-primary-text)',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '12px',
            }}
          >
            重试连接
          </button>
        )}
      </div>
    );
  }

  if (state === 'empty' || processedData.length === 0) {
    return (
      <div className="table-state-container empty-state" style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '28px', marginBottom: '8px' }}>📭</div>
        <div style={{ color: 'var(--text-secondary)', fontWeight: 500, fontSize: '14px' }}>
          {searchQuery ? `未找到匹配 "${searchQuery}" 的标的数据` : emptyMessage}
        </div>
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            style={{
              marginTop: '12px',
              padding: '4px 12px',
              background: 'transparent',
              border: '1px solid var(--border-subtle)',
              borderRadius: '4px',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            清除筛选
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="quantitative-data-table-wrapper" style={{ width: '100%' }}>
      {/* Table Toolbar */}
      <div
        className="table-toolbar"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {title && (
            <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
              {title}
            </h3>
          )}
          {state === 'stale' && (
            <span
              style={{
                fontSize: '11px',
                padding: '2px 6px',
                borderRadius: '4px',
                background: 'var(--badge-warning-bg)',
                color: 'var(--color-warning)',
                border: '1px solid var(--badge-warning-border)',
              }}
            >
              ● 缓存数据 (STALE)
            </span>
          )}
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            共 {processedData.length} 标的
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {/* Top Page Size Quick Selector (30 / 50 / 100 / 200) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>每页:</span>
            {(pageSizeOptions || [30, 50, 100, 200]).map((sz) => (
              <button
                key={sz}
                type="button"
                id={`btn-pagesize-top-${sz}`}
                onClick={() => {
                  setCurrentPageSize(sz);
                  setCurrentPage(1);
                }}
                style={{
                  padding: '3px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: currentPageSize === sz ? 600 : 400,
                  border: currentPageSize === sz ? '1px solid var(--accent-cyan)' : '1px solid var(--border-subtle)',
                  background: currentPageSize === sz ? 'var(--badge-info-bg)' : 'var(--bg-card)',
                  color: currentPageSize === sz ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {sz}条
              </button>
            ))}
          </div>

          {searchFilter && (
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder={searchPlaceholder}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid var(--border-subtle)',
                background: 'var(--bg-card)',
                color: 'var(--text-primary)',
                fontSize: '12px',
                outline: 'none',
                minWidth: '200px',
              }}
            />
          )}
          {headerAction}
        </div>
      </div>

      {/* Main Table */}
      <div
        className="table-scroll-container"
        style={{
          overflowX: 'auto',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-card)',
          borderRadius: '8px',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: 'var(--bg-base)', borderBottom: '1px solid var(--border-subtle)' }}>
              {columns.map((col) => {
                const isSorted = sortKey === col.key;
                return (
                  <th
                    key={col.key}
                    onClick={() => col.sortable && handleSort(col.key)}
                    style={{
                      padding: '10px 12px',
                      textAlign: col.align || 'left',
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      cursor: col.sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      width: col.width,
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      {col.title}
                      {col.sortable && (
                        <span style={{ fontSize: '10px', opacity: isSorted ? 1 : 0.4 }}>
                          {isSorted ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageData.map((row, idx) => (
              <tr
                key={rowKey(row, idx)}
                style={{
                  borderBottom: '1px solid var(--border-subtle)',
                  transition: 'background 0.1s ease',
                }}
                className="data-table-row"
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      padding: '8px 12px',
                      textAlign: col.align || 'left',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col.render ? col.render(row, idx) : (row as any)[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Bar */}
      {processedData.length > 0 && (
        <div
          className="table-pagination"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            marginTop: '16px',
            padding: '8px 12px',
            background: 'var(--bg-card)',
            borderRadius: '6px',
            border: '1px solid var(--border-subtle)',
            fontSize: '12px',
            color: 'var(--text-muted)',
          }}
        >
          {/* Left: summary info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>
              共 <strong style={{ color: 'var(--text-primary)' }}>{processedData.length}</strong> 条记录
            </span>
            <span>·</span>
            <span>
              显示第 {(currentPage - 1) * currentPageSize + 1} - {Math.min(currentPage * currentPageSize, processedData.length)} 条
            </span>
          </div>

          {/* Right: Page size selector and page navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>每页展示:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                {(pageSizeOptions || [30, 50, 100, 200]).map((sz) => (
                  <button
                    key={sz}
                    type="button"
                    id={`btn-pagesize-bottom-${sz}`}
                    onClick={() => {
                      setCurrentPageSize(sz);
                      setCurrentPage(1);
                    }}
                    style={{
                      padding: '3px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: currentPageSize === sz ? 600 : 400,
                      border: currentPageSize === sz ? '1px solid var(--accent-cyan)' : '1px solid var(--border-subtle)',
                      background: currentPageSize === sz ? 'var(--badge-info-bg)' : 'var(--bg-surface-elevated)',
                      color: currentPageSize === sz ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {sz}条
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={{
                  padding: '3px 8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.4 : 1,
                  fontSize: '11px',
                }}
              >
                首页
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '3px 8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.4 : 1,
                  fontSize: '11px',
                }}
              >
                上一页
              </button>
              <span style={{ fontWeight: 500, color: 'var(--text-primary)', minWidth: '48px', textAlign: 'center' }}>
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '3px 8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.4 : 1,
                  fontSize: '11px',
                }}
              >
                下一页
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '3px 8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.4 : 1,
                  fontSize: '11px',
                }}
              >
                末页
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
